import { useState, useCallback } from "react";

import axios from "axios";

import applicationApi from "@domains/application/module/api/applicationApi";
import { ApplicationEndpoints } from "@generated/application";
import { Application, StackStatus } from "src/domains/application/types";
import { orgSessionManager } from "src/domains/auth/services/orgSessionManager";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import { useAppNavigation } from "src/platform/hooks/useAppNavigation";

interface UseStackActionsParams {
  sysAlias: string | undefined;
  orgAlias: string | undefined;
  projectAlias: string | undefined;
  system: Application | null;
  setSystem: React.Dispatch<React.SetStateAction<Application | null>>;
}

interface UseStackActionsReturn {
  // Shared loading / error state
  actionLoading: boolean;
  actionError: string | null;
  clearActionError: () => void;
  deployMessage: string | null;
  clearDeployMessage: () => void;

  // Individual loading flags for non-actionLoading flows
  retryProvisioningLoading: boolean;
  deleteProtectionLoading: boolean;
  autoDeployLoading: boolean;
  promoteLoading: boolean;

  // Dialog state
  deleteDialogOpen: boolean;
  setDeleteDialogOpen: (open: boolean) => void;

  // Action handlers
  pause: () => Promise<void>;
  resume: () => Promise<void>;
  deploy: () => Promise<void>;
  buildAndDeploy: () => Promise<void>;
  buildOnly: () => Promise<void>;
  provisionVcs: () => Promise<void>;
  retryProvisioning: () => Promise<void>;
  softDelete: () => Promise<void>;
  hardDelete: () => Promise<void>;
  deleteProtectionChange: (enabled: boolean) => Promise<void>;
  autoDeployChange: (enabled: boolean) => Promise<void>;
  promote: (targetSlug: string) => Promise<void>;
}

const authHeader = () => ({
  Authorization: `Bearer ${orgSessionManager.getToken() ?? ""}`,
});

export function useStackActions({
  sysAlias,
  orgAlias,
  projectAlias,
  system,
  setSystem,
}: UseStackActionsParams): UseStackActionsReturn {
  const nav = useAppNavigation();

  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [deployMessage, setDeployMessage] = useState<string | null>(null);

  const [retryProvisioningLoading, setRetryProvisioningLoading] =
    useState(false);
  const [deleteProtectionLoading, setDeleteProtectionLoading] = useState(false);
  const [autoDeployLoading, setAutoDeployLoading] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const clearActionError = useCallback(() => setActionError(null), []);
  const clearDeployMessage = useCallback(() => setDeployMessage(null), []);

  const pause = useCallback(async () => {
    if (!sysAlias || !system) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await axios.post(
        `${apiBaseUrls.BACKEND}${ApplicationEndpoints.PAUSE_APPLICATION.replace(":id", sysAlias)}`,
        {},
        { headers: authHeader() },
      );
      setSystem((s) => (s ? { ...s, status: StackStatus.STOPPED } : s));
    } catch {
      setActionError("Failed to pause stack. Please try again.");
    } finally {
      setActionLoading(false);
    }
  }, [sysAlias, system, setSystem]);

  const resume = useCallback(async () => {
    if (!sysAlias || !system) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await axios.post(
        `${apiBaseUrls.BACKEND}${ApplicationEndpoints.RESUME_APPLICATION.replace(":id", sysAlias)}`,
        {},
        { headers: authHeader() },
      );
      setSystem((s) => (s ? { ...s, status: StackStatus.RUNNING } : s));
    } catch {
      setActionError("Failed to resume stack. Please try again.");
    } finally {
      setActionLoading(false);
    }
  }, [sysAlias, system, setSystem]);

  const deploy = useCallback(async () => {
    if (!sysAlias) return;
    setActionLoading(true);
    setActionError(null);
    setDeployMessage(null);
    try {
      await applicationApi.deployOnly(sysAlias);
      setDeployMessage("Deploy triggered — stack will update shortly.");
      setSystem((s) => (s ? { ...s, status: StackStatus.PROVISIONING } : s));
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to trigger deploy";
      setActionError(errorMessage);
    } finally {
      setActionLoading(false);
    }
  }, [sysAlias, setSystem]);

  const buildAndDeploy = useCallback(async () => {
    if (!sysAlias) return;
    setActionLoading(true);
    setActionError(null);
    setDeployMessage(null);
    try {
      const result = await applicationApi.buildAndDeploy(sysAlias);
      setDeployMessage(`Build & deploy queued. Job: ${result.jobId}`);
      setSystem((s) => (s ? { ...s, status: StackStatus.PROVISIONING } : s));
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to trigger build & deploy";
      setActionError(errorMessage);
    } finally {
      setActionLoading(false);
    }
  }, [sysAlias, setSystem]);

  const buildOnly = useCallback(async () => {
    if (!sysAlias) return;
    setActionLoading(true);
    setActionError(null);
    setDeployMessage(null);
    try {
      const result = await applicationApi.buildApplication(sysAlias);
      setDeployMessage(
        `Build queued — images will be pushed to the registry. Job: ${result.jobId}`,
      );
      setSystem((s) => (s ? { ...s, status: StackStatus.PROVISIONING } : s));
    } catch {
      setActionError("Failed to trigger build. Please try again.");
    } finally {
      setActionLoading(false);
    }
  }, [sysAlias, setSystem]);

  const provisionVcs = useCallback(async () => {
    if (!sysAlias) return;
    setActionLoading(true);
    setActionError(null);
    setDeployMessage(null);
    try {
      await axios.post(
        `${apiBaseUrls.BACKEND}/api/v1/applications/${sysAlias}/provision-vcs`,
        {},
        { headers: authHeader() },
      );
      setDeployMessage(
        "VCS provisioning started. This may take a few minutes...",
      );
      setSystem((s) => (s ? { ...s, status: StackStatus.PROVISIONING } : s));
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to trigger VCS provisioning";
      setActionError(errorMessage);
    } finally {
      setActionLoading(false);
    }
  }, [sysAlias, setSystem]);

  const retryProvisioning = useCallback(async () => {
    if (!sysAlias) return;
    setRetryProvisioningLoading(true);
    setActionError(null);
    try {
      await axios.post(
        `${apiBaseUrls.BACKEND}${ApplicationEndpoints.RETRY_PROVISIONING.replace(":id", sysAlias)}`,
        {},
        { headers: authHeader() },
      );
      setSystem((s) => (s ? { ...s, status: StackStatus.PROVISIONING } : s));
    } catch {
      setActionError("Failed to retry VCS provisioning. Please try again.");
    } finally {
      setRetryProvisioningLoading(false);
    }
  }, [sysAlias, setSystem]);

  const softDelete = useCallback(async () => {
    if (!sysAlias) return;
    setActionLoading(true);
    try {
      await axios.delete(
        `${apiBaseUrls.BACKEND}${ApplicationEndpoints.DELETE_APPLICATION.replace(":id", sysAlias)}`,
        { headers: authHeader() },
      );
      setDeleteDialogOpen(false);
      if (orgAlias && projectAlias) {
        nav.goToProjectDetails(orgAlias, projectAlias);
      }
    } catch {
      setActionError("Failed to archive stack. Please try again.");
      setDeleteDialogOpen(false);
    } finally {
      setActionLoading(false);
    }
  }, [sysAlias, orgAlias, projectAlias, nav]);

  const hardDelete = useCallback(async () => {
    if (!sysAlias) return;
    setActionLoading(true);
    try {
      await axios.delete(
        `${apiBaseUrls.BACKEND}${ApplicationEndpoints.DELETE_APPLICATION.replace(":id", sysAlias)}?hard=true`,
        { headers: authHeader() },
      );
      setDeleteDialogOpen(false);
      if (orgAlias && projectAlias) {
        nav.goToProjectDetails(orgAlias, projectAlias);
      }
    } catch {
      setActionError("Failed to delete stack. Please try again.");
      setDeleteDialogOpen(false);
    } finally {
      setActionLoading(false);
    }
  }, [sysAlias, orgAlias, projectAlias, nav]);

  const deleteProtectionChange = useCallback(
    async (enabled: boolean) => {
      if (!sysAlias) return;
      setDeleteProtectionLoading(true);
      try {
        await axios.patch(
          `${apiBaseUrls.BACKEND}${ApplicationEndpoints.SET_DELETE_PROTECTION.replace(":id", sysAlias)}`,
          { enabled },
          { headers: authHeader() },
        );
        setSystem((s) => (s ? { ...s, deleteProtection: enabled } : s));
      } catch {
        setActionError("Failed to update delete protection.");
      } finally {
        setDeleteProtectionLoading(false);
      }
    },
    [sysAlias, setSystem],
  );

  const autoDeployChange = useCallback(
    async (enabled: boolean) => {
      if (!sysAlias) return;
      setAutoDeployLoading(true);
      try {
        await axios.patch(
          `${apiBaseUrls.BACKEND}/api/v1/applications/${sysAlias}`,
          { autoDeploy: enabled },
          { headers: authHeader() },
        );
        setSystem((s) => (s ? { ...s, autoDeploy: enabled } : s));
      } catch {
        setActionError("Failed to update auto-deploy setting.");
      } finally {
        setAutoDeployLoading(false);
      }
    },
    [sysAlias, setSystem],
  );

  const [promoteLoading, setPromoteLoading] = useState(false);
  const promote = useCallback(
    async (targetSlug: string) => {
      if (!sysAlias) return;
      setPromoteLoading(true);
      setActionError(null);
      setDeployMessage(null);
      try {
        const { data } = await axios.post(
          `${apiBaseUrls.BACKEND}/api/v1/applications/${sysAlias}/promote`,
          { targetEnvironmentSlug: targetSlug },
          { headers: authHeader() },
        );
        setDeployMessage(
          `Promote to ${data.targetEnvironment ?? targetSlug} queued. Job: ${data.jobId}`,
        );
        setSystem((s) => (s ? { ...s, status: StackStatus.PROVISIONING } : s));
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to promote stack";
        setActionError(errorMessage);
      } finally {
        setPromoteLoading(false);
      }
    },
    [sysAlias, setSystem],
  );

  return {
    actionLoading,
    actionError,
    clearActionError,
    deployMessage,
    clearDeployMessage,

    retryProvisioningLoading,
    deleteProtectionLoading,
    autoDeployLoading,
    promoteLoading,

    deleteDialogOpen,
    setDeleteDialogOpen,

    pause,
    resume,
    deploy,
    buildAndDeploy,
    buildOnly,
    provisionVcs,
    retryProvisioning,
    softDelete,
    hardDelete,
    deleteProtectionChange,
    autoDeployChange,
    promote,
  };
}

export default useStackActions;
